import React, { useContext, useState } from "react";
import {
  Typography,
  Space,
  Input,
  Select,
  Button,
  Divider,
  Form,
  Upload,
  message,
  Image,
  Spin,
} from "antd";
import { Flex } from "antd";
import { categories } from "../../../constant/phoneNumbers";
import {
  MinusCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import {
  metaAssetHandler,
  updateWhatsappBusinessProfile,
} from "../../../../../reduxToolkit/channelSlice";
import { useFileUploader } from "../../../../../hooks/useFileUploader";
import NotificationContext from "../../../../../providers/NotificationProvider";
import { showSuccessToast } from "../../../../../components/commonComponents/gxAntdToast/toastUtils";

const { Title, Text } = Typography;
const { TextArea } = Input;

const BusinessNumberAction = ({
  // onCancel,
  // onSubmit,
  about,
  email,
  address,
  description,
  phone_number_id,
  profile_picture_url,
  profile_name,
  vertical,
  websites = [],
  onSuccess,
}) => {
  const {
    updateBusinessProfileLoading,
    metaAssetHandlerLoading,
    metaAssetHandlerError,
  } = useSelector((store) => store.Channel);
  const { currentBrand } = useSelector((store) => store.app);

  const brand_id = currentBrand?.brand_id;
  // Prepare initial values for the form
  const dispatch = useDispatch();
  const toast = useContext(NotificationContext);

  const initialValues = {
    profile_name,
    vertical,
    description,
    address,
    email,
    about,
    websites: websites.length ? websites : [""],
  };

  const [form] = Form.useForm();
  const [previewImage, setPreviewImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(
    profile_picture_url || null,
  );
  const [imageChanged, setImageChanged] = useState(false);
  const [metaImageUrl, setMetaImageUrl] = useState(null);

  const {
    uploadFiles,
    responseFile: uploadedFileUrl,
    error: uploadError,
    uploading, // Add this
  } = useFileUploader();

  const handleDeleteImage = () => {
    setCurrentImageUrl(null);
    setImageChanged(true);
    setPreviewImage(null);
    form.setFieldsValue({ profile_picture_url: null });
  };

  const handlePreviewImage = () => {
    setShowPreview(true);
  };

  const handleImageUpload = async ({ file, onSuccess }) => {
    try {
      const uploadedUrl = await uploadFiles(
        { target: { files: [file] } },
        "IMAGE",
        null,
        { brand_id },
      );

      if (uploadedUrl) {
        setPreviewImage(uploadedUrl);
        setCurrentImageUrl(uploadedUrl);

        // Modified parameters for uploadFileToMeta
        const metaResponse = await dispatch(
          metaAssetHandler({
            brandId: brand_id,
            cloudUrl: uploadedUrl,
          }),
        );

        if (metaResponse?.type === "whatsapp/meta_asset_handler/fulfilled") {
          if (metaResponse?.payload?.meta_upload_response) {
            setMetaImageUrl(metaResponse.payload.meta_upload_response);
          }
        }

        setImageChanged(true);
        form.setFieldsValue({ profile_picture_url: uploadedUrl });
        onSuccess("ok");
      } else if (uploadError) {
        message.error("Failed to upload image");
        throw new Error("Upload failed"); // This will trigger the catch block
      }
    } catch (error) {
      message.error("Failed to upload image");
      // Clear loading states
      // const action = {
      //   type: "whatsapp/upload_files_rejected",
      //   payload: { error: error.message },
      // };
      // dispatch(action);
    }
  };

  const handleFinish = async (values) => {
    const body = {
      vertical: values.vertical,
      description: values.description,
      address: values.address,
      email: values.email,
      ...(values.about?.trim() && { about: values.about }),
      websites: values.websites,
    };

    // Use Meta URL for submission, but display local URL
    if (imageChanged) {
      body.profile_picture_url = metaImageUrl || currentImageUrl;
    }

    try {
      const resultAction = await dispatch(
        updateWhatsappBusinessProfile({
          phone_number_id,
          brand_id,
          body,
          messaging_product: "whatsapp",
        }),
      );
      if (resultAction?.type?.endsWith("/fulfilled")) {
        if (onSuccess) onSuccess();
        showSuccessToast(toast, "Profile updated");
      }
    } catch (e) {
      // Optionally handle error
    }
  };

  return (
    <>
      <Text className="text-xs text-[#616874]">
        This will be visible on your business profile
      </Text>
      <Divider style={{ margin: "12px 0" }} />
      <Spin
        spinning={uploading || metaAssetHandlerLoading}
        tip="Uploading image..."
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleFinish}
        >
          <Flex vertical gap={24}>
            {/* Avatar & Basic Info */}
            <Flex className="" gap={16}>
              {currentImageUrl ? (
                <div className="relative w-32 h-32 group cursor-pointer">
                  <Image
                    src={currentImageUrl}
                    alt="Profile Preview"
                    width={128}
                    height={128}
                    className="rounded-lg object-cover"
                    preview={false}
                  />
                  <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 bg-black/35 flex items-center justify-center gap-4 transition-opacity duration-200 z-10">
                    <EyeOutlined
                      className="text-white text-[22px] cursor-pointer hover:scale-110 transition-transform"
                      onClick={handlePreviewImage}
                    />
                    <DeleteOutlined
                      className="text-white text-[22px] cursor-pointer hover:scale-110 transition-transform"
                      onClick={handleDeleteImage}
                    />
                  </div>
                  <Image.PreviewGroup
                    preview={{
                      visible: showPreview,
                      onVisibleChange: (vis) => setShowPreview(vis),
                    }}
                  >
                    <Image src={currentImageUrl} style={{ display: "none" }} />
                  </Image.PreviewGroup>
                </div>
              ) : (
                <Upload.Dragger
                  name="profile_picture"
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isJpg = file.type === "image/jpeg";
                    if (!isJpg) {
                      message.error("Only JPG files are allowed!");
                      return false;
                    }
                    return true;
                  }}
                  customRequest={handleImageUpload}
                  style={{ width: 128, height: 128 }}
                  className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center"
                >
                  <Text>Upload</Text>
                </Upload.Dragger>
              )}
              <Space direction="vertical" size={8} className="flex-1">
                <Form.Item
                  label={
                    <Text className="text-xs text-[#2D3036]" strong>
                      Display Name
                    </Text>
                  }
                  name="profile_name"
                >
                  <Input
                    placeholder="Gainwix"
                    disabled
                    className="text-xs font-bold py-2 text-[#898E99] mt-1"
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <Text className="text-xs text-[#2D3036]" strong>
                      Category
                    </Text>
                  }
                  name="vertical"
                >
                  <Select
                    placeholder="Select category"
                    className="mt-1 w-full text-xs  text-[#898E99]"
                  >
                    {categories.map(({ value, name, icon: Icon }) => (
                      <Select.Option key={value} value={value}>
                        <Flex align="center" className="space-x-2">
                          <Icon size={16} />
                          <Text>{name}</Text>
                        </Flex>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Space>
            </Flex>

            {/* Description */}
            <Form.Item
              label={
                <Text strong className="text-xs">
                  Description
                </Text>
              }
              name="description"
            >
              <TextArea
                autoSize={{ minRows: 3, maxRows: 5 }}
                showCount
                placeholder="Enter description"
                className="text-xs mt-1"
                maxLength={512}
              />
            </Form.Item>

            {/* About */}
            <Form.Item
              label={
                <Text strong className="text-xs">
                  About
                </Text>
              }
              name="about"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.trim().length === 0) {
                      return Promise.resolve();
                    }
                    if (/\\u[0-9a-fA-F]{4}/.test(value)) {
                      return Promise.reject(new Error('Unicode escape sequences are not supported. Please use actual emoji characters instead.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              validateTrigger={['onChange', 'onBlur']}
            >
              <TextArea
                autoSize={{ minRows: 2, maxRows: 3 }}
                showCount
                placeholder="Enter about (1-139 characters)"
                className="text-xs mt-1"
                maxLength={139}
              />
            </Form.Item>

            {/* Contact Information */}
            <Space direction="vertical" size={8} className="w-full">
              <Title style={{ marginBottom: "0px" }} level={5}>
                Contact Information
              </Title>
              <Text className="text-xs text-[#616874]">
                List your contact info to connect with your business
              </Text>
              <Form.Item
                label={
                  <Text strong className="text-xs">
                    Address
                  </Text>
                }
                name="address"
              >
                <Input
                  placeholder="Enter business address"
                  className="text-xs mt-1 text-[#555555]"
                  showCount
                  maxLength={256}
                />
              </Form.Item>
              <Form.Item
                label={
                  <Text strong className="text-xs">
                    Email
                  </Text>
                }
                name="email"
                rules={[
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input
                  placeholder="Enter business email"
                  className="text-xs mt-1 text-[#555555]"
                  showCount
                  type="email"
                  maxLength={128}
                />
              </Form.Item>
              {/* Website(s) - allow up to 2 */}
              <Form.List name="websites">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, idx) => (
                      <Form.Item
                        key={field.key}
                        label={
                          idx === 0 ? (
                            <Text className="text-xs">Website</Text>
                          ) : null
                        }
                        name={field.name}
                        rules={[
                          {
                            type: "url",
                            message: "Please enter a valid URL",
                          },
                          {
                            validator: (_, value) => {
                              if (!value || /^https?:\/\//i.test(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error(
                                  "URL must start with http:// or https://",
                                ),
                              );
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder="Ex: https://yourwebsite.com"
                          className="text-xs mt-1 text-[#555555]"
                          style={{
                            fontSize: 12,
                            color: "#555555",
                            marginTop: 4,
                          }}
                          showCount
                          maxLength={256}
                          addonAfter={
                            fields.length > 1 && (
                              <MinusCircleOutlined
                                onClick={() => remove(field.name)}
                              />
                            )
                          }
                        />
                      </Form.Item>
                    ))}
                    {fields.length < 2 && (
                      <Button
                        type="text"
                        className="pl-0"
                        onClick={() => add()}
                      >
                        <Text className="text-xs text-[#616874]">
                          + Add Another Website
                        </Text>
                      </Button>
                    )}
                  </>
                )}
              </Form.List>
            </Space>

            {/* Footer Button */}
            <Flex justify="end">
              <Button
                loading={
                  updateBusinessProfileLoading ||
                  uploading ||
                  metaAssetHandlerLoading
                }
                disabled={
                  updateBusinessProfileLoading ||
                  uploading ||
                  metaAssetHandlerLoading
                }
                type="primary"
                htmlType="submit"
              >
                Edit Profile Info
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Spin>
    </>
  );
};

export default BusinessNumberAction;
